import React, { useState, useMemo, useEffect } from 'react';
import './OrderHistory.css';

function OrderHistory({ orders, onCancelOrder, title = "Order History", showType = "orders", compact = false, isMobile = false, realTimeUpdates = false }) {
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Update timestamp when orders change (for real-time updates)
  useEffect(() => {
    if (realTimeUpdates) {
      setLastUpdate(Date.now());
    }
  }, [orders, realTimeUpdates]);

  // Safe order access helper
  const getOrderValue = (order, key, defaultValue = '') => {
    return order && order[key] !== undefined ? order[key] : defaultValue;
  };

  // Filter and sort orders
  const { filteredOrders, stats } = useMemo(() => {
    let filtered = Array.isArray(orders) ? [...orders] : [];
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(order => getOrderValue(order, 'status') === filterStatus);
    }

    // Apply tab filter
    if (activeTab !== 'all') {
      filtered = filtered.filter(order => {
        const status = getOrderValue(order, 'status');
        switch (activeTab) {
          case 'open': return ['open', 'pending', 'partial'].includes(status);
          case 'filled': return status === 'filled';
          case 'cancelled': return ['cancelled', 'rejected'].includes(status);
          default: return true;
        }
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = getOrderValue(a, sortConfig.key);
        const bVal = getOrderValue(b, sortConfig.key);
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Calculate stats
    const totalOrders = filtered.length;
    const filledOrders = filtered.filter(o => getOrderValue(o, 'status') === 'filled').length;
    const openOrders = filtered.filter(o => ['open', 'pending', 'partial'].includes(getOrderValue(o, 'status'))).length;
    const cancelledOrders = filtered.filter(o => ['cancelled', 'rejected'].includes(getOrderValue(o, 'status'))).length;
    const totalVolume = filtered.reduce((sum, order) => sum + (parseFloat(getOrderValue(order, 'total', 0)) || 0), 0);

    return {
      filteredOrders: filtered,
      stats: { totalOrders, filledOrders, openOrders, cancelledOrders, totalVolume }
    };
  }, [orders, sortConfig, filterStatus, activeTab, lastUpdate]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const canCancelOrder = (order) => {
    return order && ['pending', 'open', 'partial'].includes(getOrderValue(order, 'status'));
  };

  const getStatusVariant = (status) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'filled': return 'success';
      case 'open': return 'primary';
      case 'partial': return 'warning';
      case 'pending': return 'info';
      case 'cancelled': return 'error';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'filled': return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C13.6569 3 15.1569 3.67343 16.364 4.7231" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
      case 'open': return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
      case 'partial': return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
      case 'pending': return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
      case 'cancelled': return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
      case 'rejected': return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" 
                stroke="currentColor" strokeWidth="2"/>
          <path d="M15 9L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
      default: return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
  };

  const getTypeVariant = (type) => {
    return type === 'buy' ? 'success' : 'error';
  };

  const getTypeIcon = (type) => {
    return type === 'buy' ? (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 12L12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 12L12 19L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '--';
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch (error) {
      return '--';
    }
  };

  const formatAmount = (amount, currency = '') => {
    const num = parseFloat(amount) || 0;
    if (num === 0) return '0';
    if (num < 0.001) return num.toFixed(6);
    if (num < 1) return num.toFixed(4);
    if (num < 1000) return num.toFixed(2);
    return num.toFixed(2);
  };

  const formatPrice = (price) => {
    const num = parseFloat(price) || 0;
    if (num === 0) return '0.00';
    if (num < 0.001) return num.toFixed(6);
    if (num < 1) return num.toFixed(4);
    return num.toFixed(2);
  };

  // Professional Card Layout
  const renderOrderCard = (order, index) => {
    const type = getOrderValue(order, 'type');
    const status = getOrderValue(order, 'status');
    const pair = getOrderValue(order, 'pair', '--/--');
    const [baseCurrency, quoteCurrency] = pair.split('/');
    const timestamp = getOrderValue(order, 'timestamp') || getOrderValue(order, 'created_at') || getOrderValue(order, 'date');
    const filledAmount = parseFloat(getOrderValue(order, 'filled_amount', 0));
    const totalAmount = parseFloat(getOrderValue(order, 'amount', 0));
    const fillPercentage = totalAmount > 0 ? Math.min((filledAmount / totalAmount) * 100, 100) : 0;

    // Check if this is a recent update (last 5 seconds)
    const isRecent = realTimeUpdates && (Date.now() - new Date(timestamp).getTime() < 5000);

    return (
      <div key={order?.id || index} className={`order-card ${isRecent ? 'recent-update' : ''}`}>
        {isRecent && <div className="realtime-indicator"></div>}
        
        {/* Card Header */}
        <div className="card-header">
          <div className="order-meta">
            <div className={`order-type type-${getTypeVariant(type)}`}>
              <span className="type-icon">{getTypeIcon(type)}</span>
              <span className="type-label">{type?.toUpperCase() || 'UNKNOWN'}</span>
            </div>
            <div className="order-pair">
              {pair}
            </div>
          </div>
          <div className="order-time">
            {formatDate(timestamp)}
          </div>
        </div>

        {/* Card Body */}
        <div className="card-body">
          <div className="order-details-grid">
            <div className="detail-item">
              <label>Price</label>
              <span className="detail-value price">
                {getOrderValue(order, 'price') 
                  ? `$${formatPrice(getOrderValue(order, 'price'))}`
                  : 'Market'
                }
              </span>
            </div>
            
            <div className="detail-item">
              <label>Amount</label>
              <span className="detail-value amount">
                {formatAmount(getOrderValue(order, 'amount', 0))} {baseCurrency}
              </span>
            </div>
            
            <div className="detail-item">
              <label>Total</label>
              <span className="detail-value total">
                ${formatAmount(getOrderValue(order, 'total', 0))}
              </span>
            </div>
          </div>

          {/* Progress Bar for Partial Fills */}
          {status === 'partial' && fillPercentage > 0 && (
            <div className="fill-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${fillPercentage}%` }}
                ></div>
              </div>
              <div className="progress-text">
                {fillPercentage.toFixed(1)}% filled ({formatAmount(filledAmount)} {baseCurrency})
              </div>
            </div>
          )}
        </div>

        {/* Card Footer */}
        <div className="card-footer">
          <div className={`status-badge status-${getStatusVariant(status)}`}>
            <span className="status-icon">{getStatusIcon(status)}</span>
            <span className="status-label">
              {status === 'pending' ? 'Trading' : status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </div>
          
          {showType === 'orders' && canCancelOrder(order) && (
            <button
              className="cancel-button"
              onClick={() => onCancelOrder(getOrderValue(order, 'id'))}
            >
              <span className="cancel-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  };

  // Mobile Optimized Card
  const renderMobileOrderCard = (order, index) => {
    const type = getOrderValue(order, 'type');
    const status = getOrderValue(order, 'status');
    const pair = getOrderValue(order, 'pair', '--/--');
    const [baseCurrency] = pair.split('/');
    const timestamp = getOrderValue(order, 'timestamp') || getOrderValue(order, 'created_at') || getOrderValue(order, 'date');

    // Check if this is a recent update (last 5 seconds)
    const isRecent = realTimeUpdates && (Date.now() - new Date(timestamp).getTime() < 5000);

    return (
      <div key={order?.id || index} className={`mobile-order-card ${isRecent ? 'recent-update' : ''}`}>
        {isRecent && <div className="realtime-indicator"></div>}
        
        <div className="mobile-card-header">
          <div className="mobile-order-type">
            <span className={`type-badge type-${getTypeVariant(type)}`}>
              {getTypeIcon(type)} {type?.toUpperCase() || 'UNKNOWN'}
            </span>
            <span className="mobile-pair">{pair}</span>
          </div>
          <div className="mobile-time">{formatDate(timestamp)}</div>
        </div>

        <div className="mobile-card-body">
          <div className="mobile-details">
            <div className="mobile-detail">
              <span className="mobile-label">Price</span>
              <span className="mobile-value">
                {getOrderValue(order, 'price') 
                  ? `$${formatPrice(getOrderValue(order, 'price'))}`
                  : 'Market'
                }
              </span>
            </div>
            <div className="mobile-detail">
              <span className="mobile-label">Amount</span>
              <span className="mobile-value">
                {formatAmount(getOrderValue(order, 'amount', 0))} {baseCurrency}
              </span>
            </div>
            <div className="mobile-detail">
              <span className="mobile-label">Total</span>
              <span className="mobile-value">
                ${formatAmount(getOrderValue(order, 'total', 0))}
              </span>
            </div>
          </div>
        </div>

        <div className="mobile-card-footer">
          <div className={`mobile-status status-${getStatusVariant(status)}`}>
            <span className="status-icon">{getStatusIcon(status)}</span>
            <span className="status-label">
              {status === 'pending' ? 'Trading' : status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </div>
          
          {showType === 'orders' && canCancelOrder(order) && (
            <button
              className="mobile-cancel-button"
              onClick={() => onCancelOrder(getOrderValue(order, 'id'))}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  };

  const tabs = [
    { key: 'all', label: 'All', count: stats.totalOrders },
    { key: 'open', label: 'Open', count: stats.openOrders },
    { key: 'filled', label: 'Filled', count: stats.filledOrders },
    { key: 'cancelled', label: 'Cancelled', count: stats.cancelledOrders },
  ];

  return (
    <div className={`order-history-professional ${compact ? 'compact-view' : ''} ${isMobile ? 'mobile-view' : ''}`}>
      {/* Header Section */}
      <div className="ohp-header">
        <div className="ohp-title-section">
          <div className="title-main">
            <h2 className="ohp-title">{title}</h2>
            {realTimeUpdates && (
              <div className="realtime-badge">
                <span className="realtime-dot"></span>
                Live Updates
              </div>
            )}
            {showType === 'trades' && (
              <div className="volume-badge">
                <span className="volume-label">Total Volume</span>
                <span className="volume-value">${formatAmount(stats.totalVolume)}</span>
              </div>
            )}
          </div>
          <div className="stats-overview">
            <div className="stat-card total">
              <div className="stat-value">{stats.totalOrders}</div>
              <div className="stat-label">Total</div>
            </div>
            <div className="stat-card filled">
              <div className="stat-value">{stats.filledOrders}</div>
              <div className="stat-label">Filled</div>
            </div>
            <div className="stat-card open">
              <div className="stat-value">{stats.openOrders}</div>
              <div className="stat-label">Open</div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="ohp-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-label">{tab.label}</span>
              {tab.count > 0 && (
                <span className="tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content Section */}
      <div className="ohp-content">
        {!filteredOrders || filteredOrders.length === 0 ? (
          <div className="empty-state-professional">
            <div className="empty-illustration">
              {showType === 'trades' ? (
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M8 7H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
            </div>
            <h3 className="empty-title">
              No {showType === 'trades' ? 'Trades' : 'Orders'} Found
            </h3>
            <p className="empty-description">
              {activeTab !== 'all' 
                ? `No ${activeTab} ${showType === 'trades' ? 'trades' : 'orders'} found`
                : `You haven't made any ${showType === 'trades' ? 'trades' : 'orders'} yet`
              }
            </p>
            {showType === 'orders' && activeTab === 'all' && (
              <button 
                className="cta-button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Place Your First Order
              </button>
            )}
          </div>
        ) : (
          <div className="orders-grid">
            {filteredOrders.map((order, index) => 
              isMobile ? renderMobileOrderCard(order, index) : renderOrderCard(order, index)
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderHistory;